import { mapActions, mapGetters, mapMutations } from 'vuex'

const validateWordpressIds = (value) => {
  if (!value) return ''
  if (!/^\d+(\s*,\s*\d+)*$/.test(value)) return 'Use comma-separated numeric WordPress category IDs'
  return ''
}

const validatePositiveInteger = (value, message) => {
  if (!value) return ''
  if (!/^\d+$/.test(value)) return message
  return ''
}

const validateWebflowId = (value) => {
  if (!value) return ''
  if (!/^[A-Za-z0-9_-]+$/.test(value)) return 'Use a valid Webflow collection ID'
  return ''
}

const CMS_CONFIG = {
  wordpress: {
    label: 'WordPress categories',
    storedField: 'wordpress_id',
    payloadField: 'wordpressId',
    validate: validateWordpressIds
  },
  shopify: {
    label: 'Shopify blog',
    storedField: 'shopify_blog_id',
    payloadField: 'shopifyBlogId',
    validate: value => validatePositiveInteger(value, 'Use a numeric Shopify blog ID')
  },
  webflow: {
    label: 'Webflow collection',
    storedField: 'webflow_id',
    payloadField: 'webflowId',
    validate: validateWebflowId
  }
}

export default {
  name: 'HubCmsMapping',
  props: {
    contentplanId: {
      type: Number,
      required: true
    },
    topics: {
      type: Array,
      default: () => []
    },
    selectedTopic: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      dialog: false,
      isLoadingLiveTopics: false,
      isSaving: false,
      liveFetched: false,
      loadError: '',
      liveTopics: [],
      mappingRows: [],
      search: ''
    }
  },
  computed: {
    ...mapGetters([
      'activeContentplan',
      'webflowIntegration'
    ]),
    mainCmsIntegration () {
      return this.activeContentplan?.main_cms_integration || 'none'
    },
    cmsConfig () {
      return CMS_CONFIG[this.mainCmsIntegration] || null
    },
    isSupportedCms () {
      return !!this.cmsConfig
    },
    isWordpress () {
      return this.mainCmsIntegration === 'wordpress'
    },
    cmsLabel () {
      return this.cmsConfig?.label || 'CMS topics'
    },
    liveTopicOptions () {
      return this.liveTopics.map(topic => ({
        text: topic.url ? `${topic.id} - ${topic.name} (${topic.url})` : `${topic.id} - ${topic.name}`,
        value: topic.id
      }))
    },
    selectedTopicMapping () {
      if (!this.selectedTopic || !this.cmsConfig) return ''
      return this.formatMapping(this.selectedTopic[this.cmsConfig.storedField])
    },
    dirtyRows () {
      return this.mappingRows.filter(row => this.normalizeRowValue(row.value) !== this.normalizeValue(row.savedValue))
    },
    dirtyCount () {
      return this.dirtyRows.length
    },
    hasInvalidRows () {
      return this.mappingRows.some(row => this.getRowErrors(row).length)
    },
    filteredRows () {
      const query = this.normalizeSearchValue(this.search)
      if (!query) return this.mappingRows

      return this.mappingRows.filter(row => {
        return [
          row.name,
          row.description,
          row.savedValue,
          this.normalizeRowValue(row.value)
        ]
          .filter(Boolean)
          .some(value => String(value).toLowerCase().includes(query))
      })
    }
  },
  watch: {
    topics () {
      this.buildMappingRows()
    },
    mainCmsIntegration () {
      this.liveFetched = false
      this.liveTopics = []
      this.buildMappingRows()
    }
  },
  methods: {
    ...mapActions([
      'getIntegrations',
      'getShopifyBlogs',
      'getWebflowCollections',
      'getWordpressCategories',
      'topicGetList',
      'topicPut'
    ]),
    ...mapMutations([
      'setSnackbar'
    ]),
    async openDialog () {
      this.buildMappingRows()
      this.dialog = true
      if (!this.liveFetched && this.isSupportedCms) await this.loadLiveTopics(false)
    },
    async loadLiveTopics (force = false) {
      if (!this.isSupportedCms) return
      if (this.liveFetched && !force) return

      this.isLoadingLiveTopics = true
      this.loadError = ''
      try {
        this.liveTopics = await this.fetchLiveTopics()
        this.liveFetched = true
      } catch (error) {
        this.loadError = error?.response?.data?.message || error.message || 'Failed to load CMS topics'
        this.setSnackbar({ open: true, text: this.loadError, type: 'error' })
      } finally {
        this.isLoadingLiveTopics = false
      }
    },
    async fetchLiveTopics () {
      if (this.mainCmsIntegration === 'wordpress') {
        const response = await this.getWordpressCategories({ contentplanId: this.contentplanId })
        return (response?.categories || []).map(normalizeWordpressCategory).filter(topic => topic.id)
      }

      if (this.mainCmsIntegration === 'shopify') {
        const response = await this.getShopifyBlogs({ contentplanId: this.contentplanId })
        return (response?.blogs || []).map(normalizeShopifyBlog).filter(topic => topic.id)
      }

      if (this.mainCmsIntegration === 'webflow') {
        const siteId = await this.getWebflowSiteId()
        if (!siteId) throw new Error('Webflow site is not selected for this content plan')
        const response = await this.getWebflowCollections({ contentplanId: this.contentplanId, siteId })
        return (Array.isArray(response) ? response : []).map(normalizeWebflowCollection).filter(topic => topic.id)
      }

      return []
    },
    async getWebflowSiteId () {
      if (this.webflowIntegration?.wf_site_id) return this.webflowIntegration.wf_site_id
      await this.getIntegrations({ contentplanId: this.contentplanId })
      return this.webflowIntegration?.wf_site_id || null
    },
    async saveMappings () {
      if (!this.isSupportedCms || this.hasInvalidRows || !this.dirtyRows.length) return

      this.isSaving = true
      try {
        const topics = this.dirtyRows.map(row => ({
          topicId: row.topicId,
          [this.cmsConfig.payloadField]: this.normalizeRowValue(row.value) || null
        }))

        await this.topicPut({
          contentplanId: this.contentplanId,
          action: 'updateCmsIds',
          topics
        })
        await this.topicGetList({ contentplanId: this.contentplanId })
        this.buildMappingRows()
        this.setSnackbar({ open: true, text: 'CMS topic mappings saved.', type: 'success' })
      } catch (error) {
        this.setSnackbar({
          open: true,
          text: error?.response?.data?.message || error.message || 'Failed to save CMS topic mappings',
          type: 'error'
        })
      } finally {
        this.isSaving = false
      }
    },
    buildMappingRows () {
      if (!this.cmsConfig) {
        this.mappingRows = []
        return
      }

      this.mappingRows = (this.topics || []).map(topic => {
        const savedValue = this.normalizeValue(topic[this.cmsConfig.storedField])
        return {
          topicId: topic.id,
          name: topic.name || '',
          description: topic.description || '',
          savedValue,
          value: this.isWordpress ? this.splitWordpressIds(savedValue) : savedValue
        }
      })
    },
    getRowErrors (row) {
      if (!this.cmsConfig) return []
      const message = this.cmsConfig.validate(this.normalizeRowValue(row.value))
      return message ? [message] : []
    },
    normalizeRowValue (value) {
      if (this.isWordpress && Array.isArray(value)) {
        return value.flatMap(item => String(item || '').split(',')).map(item => item.trim()).filter(Boolean).join(',')
      }
      return this.normalizeValue(value)
    },
    normalizeValue (value) {
      if (value === null || value === undefined) return ''
      return String(value).trim()
    },
    normalizeSearchValue (value) {
      return this.normalizeValue(value).toLowerCase()
    },
    splitWordpressIds (value) {
      if (!value) return []
      return String(value).split(',').map(item => item.trim()).filter(Boolean)
    },
    formatMapping (value) {
      return this.normalizeValue(value)
    }
  },
  mounted () {
    this.buildMappingRows()
  }
}

const normalizeWordpressCategory = (category = {}) => ({
  id: normalizeValue(category.wordpressId || category.id),
  name: normalizeValue(category.categoryName || category.name || category.slug || category.wordpressId || category.id),
  url: normalizeValue(category.url)
})

const normalizeShopifyBlog = (blog = {}) => ({
  id: normalizeValue(blog.id),
  name: normalizeValue(blog.title || blog.handle || blog.id),
  url: normalizeValue(blog.url)
})

const normalizeWebflowCollection = (collection = {}) => ({
  id: normalizeValue(collection.id),
  name: normalizeValue(collection.displayName || collection.singularName || collection.name || collection.slug || collection.id),
  url: normalizeValue(collection.slug)
})

const normalizeValue = (value) => {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}
